/**
 * twilioSettingsAdmin.service.ts — Admin > Integrations > Twilio (legacy
 * Twilio Settings): the practice's own Twilio account, through
 * /twilio-settings. Credentials only ever come back masked (the last four
 * characters); a credential left blank on save keeps the stored one. Both
 * calls need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type TwilioCredentialKey = 'accountSid' | 'authToken' | 'apiKeySid' | 'apiKeySecret' | 'twimlAppSid';

/** Legacy's fields, in its order and with its labels. */
export const TWILIO_CREDENTIALS: { key: TwilioCredentialKey; label: string }[] = [
  { key: 'accountSid', label: 'Account SID' },
  { key: 'authToken', label: 'Auth Token' },
  { key: 'apiKeySid', label: 'API Key SID' },
  { key: 'apiKeySecret', label: 'API Key Secret' },
  { key: 'twimlAppSid', label: 'TwiML App SID' },
];

export interface TwilioCredential {
  /** Something is stored. */
  set: boolean;
  /** Dots and the last four characters ('' when it could not be read). */
  masked: string;
}

export interface TwilioSettings {
  /** False: no row, so sends use the platform's shared Twilio account. */
  configured: boolean;
  credentials: Record<TwilioCredentialKey, TwilioCredential>;
  forwardNumber: string;
  sipDomain: string;
}

export type TwilioSettingsPayload = Partial<Record<TwilioCredentialKey, string>> & {
  forwardNumber: string;
  sipDomain: string;
};

function toSettings(raw: Record<string, unknown>): TwilioSettings {
  const creds = (raw.credentials ?? {}) as Record<string, Record<string, unknown>>;
  const credentials = {} as Record<TwilioCredentialKey, TwilioCredential>;
  for (const { key } of TWILIO_CREDENTIALS) {
    const c = creds[key] ?? {};
    credentials[key] = { set: c.set === true, masked: String(c.masked ?? '') };
  }
  return {
    configured: raw.configured === true,
    credentials,
    forwardNumber: String(raw.forwardNumber ?? ''),
    sipDomain: String(raw.sipDomain ?? ''),
  };
}

export function getTwilioSettings(): Promise<TwilioSettings> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>('/twilio-settings').then((r) => toSettings(r.data.data ?? {}));
}

/** Only non-blank credentials are sent: a blank one keeps what is stored. */
export function saveTwilioSettings(p: TwilioSettingsPayload): Promise<TwilioSettings> {
  const body: Record<string, string> = { forwardNumber: p.forwardNumber.trim(), sipDomain: p.sipDomain.trim() };
  for (const { key } of TWILIO_CREDENTIALS) {
    const v = (p[key] ?? '').trim();
    if (v !== '') body[key] = v;
  }
  return apiClient.put<ApiResponse<Record<string, unknown>>>('/twilio-settings', body).then((r) => toSettings(r.data.data ?? {}));
}
